import React from "react";

import {
  Button,
  Card,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useNavigate } from "hooks";
import type { IClientEntityAllocation } from "store/types/client";

interface Props {
  entities: IClientEntityAllocation[];
  count: number;
  clientId: number;
  loading?: boolean;
}

const HEADERS = ["Entity name", "Linked deals"] as const;

const CELL_SX = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "50px"
} as const;

/** Linked entities preview table on the client overview. */
export const Entities: React.FC<Props> = ({ entities, count, clientId, loading = false }) => {
  const navigate = useNavigate();

  return (
    <Card sx={{ bgcolor: "background.paper", boxShadow: "sm", borderRadius: 8, p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Entities ({count})
        </Typography>
        <Button
          variant="text"
          size="sm"
          onClick={() => clientId && navigate({ route: "clientEntities", params: { client_id: clientId } })}
        >
          Manage &rarr;
        </Button>
      </Stack>
      {loading ? (
        <Stack sx={{ gap: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : entities.length > 0 ? (
        <TableContainer sx={{ borderRadius: "10px", border: "1px solid #dae0e2", overflow: "hidden" }}>
          <Table sx={{ color: "#0a3142" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#edf1f9" }}>
                {HEADERS.map((header) => (
                  <TableCell
                    key={header}
                    align={header === "Entity name" ? "left" : "center"}
                    sx={{ ...CELL_SX, fontSize: "15px", fontWeight: "normal" }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {entities.map((entity) => (
                <TableRow key={entity.id} sx={{ cursor: "pointer", ":hover": { backgroundColor: "#f5f7f7" } }}>
                  <TableCell sx={CELL_SX}>
                    <Stack>
                      <Typography sx={{ fontSize: "16px", color: "#0a3142" }} noWrap>
                        {entity.name}
                      </Typography>
                      {entity.group && (
                        <Typography sx={{ fontSize: "14px", color: "#898989" }} noWrap>
                          {entity.group}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {entity.linked_deals_count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No entities linked yet.
        </Typography>
      )}
    </Card>
  );
};

export default Entities;
